import { API_PATH, BASE_PATH } from '../config';
import React, { useState, useEffect } from 'react';
import {
    Box,
    ImageList,
    ImageListItem,
    CircularProgress,
    Typography,
    Alert,
    Skeleton,
    IconButton
} from '@mui/material';
import { 
    Image as ImageIcon
} from '@mui/icons-material';

// Route external image URLs through our same-origin proxy so they actually
// render in the browser (scrape-tier Bing thumbnails and other hotlinked hosts
// often come back blank due to referrer/hotlink checks or content blockers).
// Local URLs are left untouched. The target is base64url-encoded so blockers
// can't substring-match the upstream host.
const proxify = (url) => {
    if (!url || !/^https?:\/\//i.test(url)) return url;
    try {
        const b64 = btoa(url).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        return `${API_PATH}/image_proxy.php?u=${b64}`;
    } catch (e) {
        return url;
    }
};

const ActivityImageGallery = ({ location, onImageClick }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (location) {
            fetchImages();
        }
    }, [location]);

    const fetchImages = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await fetch(`${API_PATH}/activity_images.php?location=${encodeURIComponent(location)}`);
            const data = await response.json();
            
            if (data.success) {
                setImages(data.images);
            } else {
                setError(data.error || 'Failed to load images');
            }
        } catch (err) {
            setError('Error loading images: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImageClick = (image) => {
        // Open Google Images search for this location
        const searchQuery = `${location} photos`;
        const url = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchQuery)}`;
        
        window.open(url, '_blank');
        
        if (onImageClick) {
            onImageClick(image);
        }
    };

    if (loading) {
        return (
            <Box sx={{ py: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Loading images...
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, width: '100%' }}>
                    {[1, 2, 3, 4].map((n) => (
                        <Box key={n} sx={{ aspectRatio: '1' }}>
                            <Skeleton
                                variant="rectangular"
                                width="100%"
                                height="100%"
                                sx={{ borderRadius: 1 }}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="info" sx={{ my: 1 }}>
                {error}
            </Alert>
        );
    }

    if (images.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No images available for this location.
            </Typography>
        );
    }

    return (
        <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="subtitle2">
                    Photos of {location}
                </Typography>
                <IconButton 
                    size="small" 
                    onClick={() => {
                        const searchQuery = `${location} photos`;
                        const url = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchQuery)}`;
                        window.open(url, '_blank');
                    }}
                    title="Search Google Images"
                >
                    <ImageIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, width: '100%' }}>
                {images.map((image, index) => (
                    <Box 
                        key={image.id || index}
                        sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8,
                                transform: 'scale(1.02)',
                            },
                            transition: 'all 0.2s ease',
                            width: '100%',
                            aspectRatio: '1',
                            borderRadius: 1,
                            overflow: 'hidden'
                        }}
                        onClick={() => handleImageClick(image)}
                    >
                        <img
                            src={image.thumbnail_url ? proxify(image.thumbnail_url) : `${API_PATH}/serve_image.php?filename=${image.thumbnail_filename}&type=thumbnail`}
                            alt={`${location} - Image ${index + 1}`}
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </Box>
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Click any image to search Google Images
            </Typography>
        </Box>
    );
};

export default ActivityImageGallery;